/**
 * Build-time configuration (Vite `VITE_*` variables, baked into the static bundle).
 * Defaults target the local dev stack (`pnpm dev:stack`, see infra/compose/README.md).
 */
export interface OperatorConfig {
  /** API base incl. version prefix. Default `/v1` (same origin, proxied to the API). */
  apiBaseUrl: string;
  /** Unauthenticated liveness probe used as the connectivity ping. Default `/health/live`. */
  healthUrl: string;
  /** Keycloak realm issuer. */
  oidcAuthority: string;
  oidcClientId: string;
  /** Origin the app is served from (redirect URIs are derived from it). */
  appOrigin: string;
  /** Admin_Portal URL shown to staff without an operator role. */
  adminUrl: string;
  /**
   * Host of product-code QR payloads `https://{qrHost}/c/{code}` (the API's QR_HOST,
   * Req 11.7). Scanned URLs on any other host are rejected. Default `api.localhost`.
   */
  qrHost: string;
}

type Env = Partial<Record<string, string | boolean | undefined>>;

const str = (env: Env, key: string, fallback: string): string => {
  const v = env[key];
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : fallback;
};

export function readConfig(env: Env, appOrigin: string): OperatorConfig {
  return {
    apiBaseUrl: str(env, 'VITE_API_BASE_URL', '/v1'),
    healthUrl: str(env, 'VITE_HEALTH_URL', '/health/live'),
    oidcAuthority: str(env, 'VITE_OIDC_AUTHORITY', 'https://auth.localhost/realms/macksons'),
    oidcClientId: str(env, 'VITE_OIDC_CLIENT_ID', 'operator-console'),
    appOrigin,
    adminUrl: str(env, 'VITE_ADMIN_URL', 'https://admin.localhost'),
    qrHost: str(env, 'VITE_QR_HOST', 'api.localhost'),
  };
}

/** Resolves a relative URL (`/v1`, `/health/live`) against the app origin. */
export function absoluteUrl(config: OperatorConfig, url: string): string {
  return new URL(url, `${config.appOrigin}/`).toString().replace(/\/+$/, '');
}
